import * as React from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Bone } from '@lightspeed/flame/Bone';
import { Button } from '@lightspeed/flame/Button';
import { Card } from '@lightspeed/flame/Card';
import { Box, Flex } from '@lightspeed/flame/Core';
import { theme } from '@lightspeed/flame/Core/themes/flame';
import { Divider } from '@lightspeed/flame/Divider';
import { FormHelper, Label } from '@lightspeed/flame/FormField';
import { Group, GroupAddon } from '@lightspeed/flame/Group';
import { IconWarning } from '@lightspeed/flame/Icon/Warning';
import { Input } from '@lightspeed/flame/Input';
import { ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Radio, RadioLabel } from '@lightspeed/flame/Radio';
import { Text } from '@lightspeed/flame/Text';

import PaymentServiceAlert from 'app/src/controllers/payments/PaymentServiceAlert';
import CreditAccount from 'app/src/data/models/CreditAccount';
import format from 'helpers/format';

import { getPaymentLinkAmountLimits } from '../../CreditAccountStatement/utils';
import { StyledPaymentDepositModal } from './AddPaymentDepositModal.style';
import { useCheckRemoteDepositPaymentLink } from './hooks/useCheckRemoteDepositPaymentLink';
import { AddDepositModalProps, ErrorMessage, OpenOrderType } from './types';

export const ADD_PAYMENT_DEPOSIT_MODAL_ERRORS = {
  ERROR_FETCHING_LS_PAY_STATUS: {
    title: 'Remote deposit unavailable',
    message: 'Unable to fetch Lightspeed Payments at the moment. Try again later.',
  },
  ERROR_ADDING_IN_PERSON_DEPOSIT: {
    title: 'Something went wrong',
    message: 'Unable to add a deposit at the moment. Try again.',
  },
  ERROR_UPDATING_CUSTOMER_EMAIL: {
    title: 'Something went wrong',
    message: 'Unable to update customer email at the moment. Try again.',
  },
};

export const DisplayModalValues: React.FC<{ title: string; total: number; testID: string }> = ({
  title,
  total,
  testID,
}) => {
  const { currency_code: currencyCode } = window.merchantos.locale;
  const locale = window.merchantos.locale.lang;

  let convertedTotal;
  if (total <= 0 || !total) {
    convertedTotal = 0;
  } else {
    convertedTotal = total;
  }
  return (
    <Box pr={4} pb={1}>
      <Text fontSize="12px">{title}</Text>
      <Text
        fontSize="21px"
        fontWeight={500}
        color={theme.colors['gray-600']}
        pt="5px"
        className="notranslate"
        data-test={`${testID}`}
      >
        {title !== 'Open orders'
          ? format.toMoney(convertedTotal, currencyCode, locale)
          : convertedTotal}
      </Text>
    </Box>
  );
};

const AddPaymentDepositModal: React.FC<AddDepositModalProps> = ({
  openOrders,
  saleManager,
  handleCloseModal,
  isModalOpen,
  isLoading,
  currentShopHasLightspeedPayments,
  setModalView,
  setAmount,
  amount,
  radioBtnOption,
  setRadioBtnOption,
  isAccountPayment,
  isErrorFetchingLightspeedPaymentsStatus,
  setPaylinkCardOption,
}) => {
  const [inputAmount, setInputAmount] = React.useState(amount);
  const [error, setError] = React.useState<ErrorMessage | null>(null);
  const [inputError, setInputError] = React.useState<string | JSX.Element>('');
  const [selectedOption, setSelectedOption] = React.useState(radioBtnOption);
  const [depositInProgress, setDepositInProgress] = React.useState(false);

  const { currency_symbol: currency } = window.merchantos.locale;

  const { currentSaleRemoteDepositPaymentLinkRequest } = useCheckRemoteDepositPaymentLink(
    saleManager.sale.id
  );

  React.useEffect(() => {
    const el = document.getElementById('remote-payment-deposit-tile-editing');

    if (currentSaleRemoteDepositPaymentLinkRequest?.data.data.amount && el) {
      // the merchant has a remote deposit on this sale and the options/inputs should be pre-filled when they click the "edit" button
      const {
        data: {
          data: { amount, sendReceipt },
        },
      } = currentSaleRemoteDepositPaymentLinkRequest;

      setSelectedOption('remote-deposit');
      if (inputAmount && inputAmount !== '') {
        setInputAmount(inputAmount);
      } else {
        setInputAmount(amount);
      }
      setPaylinkCardOption(sendReceipt ? 'by-email' : 'paylink-only');
    }
  }, [currentSaleRemoteDepositPaymentLinkRequest, inputAmount, setAmount, setPaylinkCardOption]);

  const NON_PAY_LINK_AMOUNT_MIN_VALUE = 0.01; // min one cent

  const allOpenOrderRecord = (openOrders as OpenOrderType[]).find(
    (openOrderTotal) => openOrderTotal.type === 'all'
  ) || { type: '', total: '', remaining: '', count: '0' };

  const {
    fullName,
    creditAccountBalance,
    attributes: { customerID, creditAccountID },
  } = saleManager.customer;

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputOption = event.target.value;
    setSelectedOption(inputOption);

    if (inputOption === 'remote-deposit') {
      checkRemoteDepositError(inputAmount);
    } else {
      checkInPersonError(inputAmount);
    }
  };

  const handleDepositAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    if (selectedOption === 'remote-deposit') {
      checkRemoteDepositError(inputValue);
    } else {
      checkInPersonError(inputValue);
    }
  };

  const checkInPersonError = (value: string) => {
    const decimalCount = (value.split('.')[1] || '').length;

    if (!value || parseFloat(value) < NON_PAY_LINK_AMOUNT_MIN_VALUE) {
      setInputError(
        `${isAccountPayment ? 'Payment' : 'Deposit'} amount must be greater than ${currency}0.00`
      );
      setInputAmount(value);
    } else if (decimalCount > 2) {
      setInputError('');
    } else {
      setInputAmount(value);
      setInputError('');
    }
  };

  const checkRemoteDepositError = (value: string) => {
    const decimalCount = (value.split('.')[1] || '').length;

    const inputError = (
      <Text data-test="lightspeed-payments-max-limit-warning">
        Lightspeed payment links have a limit of{' '}
        <var className="notranslate">
          {currency}
          {getPaymentLinkAmountLimits().max.toLocaleString()}
        </var>
        . If you need to collect more than{' '}
        <var className="notranslate">
          {currency}
          {getPaymentLinkAmountLimits().max.toLocaleString()}
        </var>{' '}
        by payment link, you can generate multiple account statements.
      </Text>
    );

    if (parseFloat(value) > getPaymentLinkAmountLimits().max) {
      setInputError(inputError);
      setInputAmount(value);
    } else if (!value || parseFloat(value) < getPaymentLinkAmountLimits().min) {
      setInputError('Amount due must be more than $0.50 to create a payment link.');
      setInputAmount(value);
    } else if (decimalCount > 2) {
      setInputError('');
    } else {
      setInputAmount(value);
      setInputError('');
    }
  };

  const handleCreditAccount = () => {
    if (creditAccountID) {
      handleAddDeposit();
    } else {
      const creditAccount = new CreditAccount({
        name: fullName,
        customerID,
      });

      creditAccount.unset('archived');
      creditAccount.unset('description');
      creditAccount.unset('giftCard');
      creditAccount.unset('balance');

      saleManager.customer.setRelation('CreditAccount', creditAccount);
      saleManager.customer.save().then(() => {
        try {
          handleAddDeposit();
        } catch (err) {
          PaymentServiceAlert.alert(err.message);
        }
      });
    }
  };

  const handleAddDeposit = async () => {
    setDepositInProgress(true);
    window.showNotifier('working');
    try {
      await saleManager.addInSaleDeposit(inputAmount);
      handleCloseModal();
    } catch (err) {
      setError({
        title: ADD_PAYMENT_DEPOSIT_MODAL_ERRORS.ERROR_ADDING_IN_PERSON_DEPOSIT.title,
        message: ADD_PAYMENT_DEPOSIT_MODAL_ERRORS.ERROR_ADDING_IN_PERSON_DEPOSIT.message,
      });
    }
    setDepositInProgress(false);
    window.hideNotifier('working');
  };

  const accountBalanceCheck = (openOrdersTotal: number, accountBalance: number) => {
    if (accountBalance > openOrdersTotal) return 0;

    return openOrdersTotal - accountBalance;
  };

  const handleModalView = () => {
    if (selectedOption === 'remote-deposit') {
      setModalView('request-deposit-paylink');
      setAmount(inputAmount);
      setRadioBtnOption(selectedOption);
    } else {
      handleCreditAccount();
    }
  };

  React.useEffect(() => {
    if (!currentShopHasLightspeedPayments && isErrorFetchingLightspeedPaymentsStatus) {
      setError({
        title: ADD_PAYMENT_DEPOSIT_MODAL_ERRORS.ERROR_FETCHING_LS_PAY_STATUS.title,
        message: ADD_PAYMENT_DEPOSIT_MODAL_ERRORS.ERROR_FETCHING_LS_PAY_STATUS.message,
      });
    }
  }, [currentShopHasLightspeedPayments, isErrorFetchingLightspeedPaymentsStatus]);

  return (
    <StyledPaymentDepositModal
      isOpen={isModalOpen}
      onRequestClose={handleCloseModal}
      shouldCloseOnOverlayClick={false}
    >
      <ModalHeader
        onCloseClicked={handleCloseModal}
        showCloseButton={false}
        data-test="deposit-paylink-modal-header"
      >
        {isLoading ? (
          <Bone height={4} width={10} data-test="bones-loading-state" />
        ) : (
          <Text>Add {isAccountPayment ? 'payment' : 'deposit'}</Text>
        )}
      </ModalHeader>
      <ModalBody>
        {error && (
          <Alert mb={3} type="danger" noCloseBtn title={error.title}>
            {error.message}
          </Alert>
        )}
        <Card p={3} mb={3}>
          {isLoading ? (
            <Box>
              <Bone height={3} width={10} style={{ marginBottom: '10px' }} />
              <Bone height={10} />
            </Box>
          ) : (
            <>
              <Text fontSize="16px" fontWeight="bold" mb="10px" className="notranslate">
                {fullName}
              </Text>
              <Box>
                <Flex flexWrap="wrap">
                  <DisplayModalValues
                    title="Account Balance"
                    total={creditAccountBalance}
                    testID="deposit-paylink-account-balance"
                  />
                  <DisplayModalValues
                    title="Open orders"
                    total={parseFloat(allOpenOrderRecord.count)}
                    testID="deposit-paylink-open-orders"
                  />
                  <DisplayModalValues
                    title="Open orders total"
                    total={parseFloat(allOpenOrderRecord.total)}
                    testID="deposit-paylink-open-orders-total"
                  />
                  <DisplayModalValues
                    title="Left to pay"
                    total={accountBalanceCheck(
                      parseFloat(allOpenOrderRecord.total),
                      creditAccountBalance
                    )}
                    testID="deposit-paylink-left-to-pay"
                  />
                </Flex>
              </Box>
            </>
          )}
        </Card>
        {isLoading ? (
          <Box>
            <Bone height={3} width={10} style={{ marginBottom: '10px' }} />
            <Bone height={8} />
          </Box>
        ) : (
          <>
            <Label>{isAccountPayment ? 'Payment' : 'Deposit'} amount</Label>
            <Group style={{ paddingBottom: '5px' }} noSpacing inputBlock>
              <GroupAddon>{currency}</GroupAddon>
              <Input
                autoFocus
                value={inputAmount}
                placeholder={`Enter a ${isAccountPayment ? 'payment' : 'deposit'} amount`}
                onChange={(event) => handleDepositAmountChange(event)}
                onBlur={(event) => handleDepositAmountChange(event)}
                type="number"
                style={{ border: 0, boxShadow: 'none' }}
                suffix={inputError ? <IconWarning color={theme.colors.warning} /> : null}
                data-test="deposit-paylink-input-amount"
                id="deposit-amount"
                disabled={depositInProgress}
              />
            </Group>
          </>
        )}
        <FormHelper pt={1} status="warning">
          {inputError || null}
        </FormHelper>
        <Divider pt={2} pb={3} color={theme.colors['gray-100']} />
        {isLoading ? (
          <Box>
            <Bone height={7} style={{ marginBottom: '10px' }} />
            <Bone height={7} />
          </Box>
        ) : null}
        {!isLoading && !isAccountPayment ? (
          <Box>
            <RadioLabel description="Collect the deposit at the counter. The amount will be added to the customer’s account.">
              <Radio
                checked={selectedOption === 'in-person-deposit'}
                value="in-person-deposit"
                data-test="deposit-paylink-in-person-deposit-radio-btn"
                onChange={(event) => handleOptionChange(event)}
              />
              In-person deposit
            </RadioLabel>
            <RadioLabel
              pt={3}
              color={
                currentShopHasLightspeedPayments ? theme.colors.black : theme.colors['gray-200']
              }
              description={
                <Text
                  color={
                    currentShopHasLightspeedPayments ? theme.colors.black : theme.colors['gray-200']
                  }
                >
                  {!currentShopHasLightspeedPayments && !isErrorFetchingLightspeedPaymentsStatus
                    ? 'Onboard on to Lightspeed Payments to collect money remotely.'
                    : 'Add a payment link to the receipt and send it to your customer. Once a payment is received, the deposit will be added to their account.'}
                </Text>
              }
            >
              <Radio
                disabled={!currentShopHasLightspeedPayments}
                checked={selectedOption === 'remote-deposit'}
                value="remote-deposit"
                data-test="deposit-paylink-remote-deposit-radio-btn"
                onChange={(event) => handleOptionChange(event)}
              />
              Remote deposit (via payment link)
            </RadioLabel>
          </Box>
        ) : null}
        {!isLoading && isAccountPayment ? (
          <Alert type="info" noCloseBtn>
            Collect money at the counter for payment toward credit account balance owed, the amount
            will be added to the customer&apos;s account instantly.
            {currentShopHasLightspeedPayments
              ? ' For remote payments towards a credit account, customers can use the payment link in their account statement.'
              : ''}
          </Alert>
        ) : null}
      </ModalBody>
      <ModalFooter>
        {isLoading ? (
          <Flex justifyContent="right">
            <Bone height={4} width={10} style={{ marginRight: '10px' }} />
            <Bone height={4} width={10} />
          </Flex>
        ) : (
          <Flex justifyContent="right">
            <Group>
              <Button id="cancel-deposit" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button
                data-test="deposit-paylink-add-deposit-btn"
                variant="secondary"
                fill
                onClick={() => handleModalView()}
                disabled={!!inputError || !inputAmount || depositInProgress}
              >
                Next
              </Button>
            </Group>
          </Flex>
        )}
      </ModalFooter>
    </StyledPaymentDepositModal>
  );
};

export default AddPaymentDepositModal;
